import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { levelLabel } from "@/lib/levels";
import { Button } from "@/components/ui/button";
import NonSpeakingPanel from "@/components/NonSpeakingPanel";
import { Lock, ArrowLeft } from "lucide-react";

function ytEmbed(url) {
  if (!url) return null;
  const m = String(url).match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

export default function GameDetail() {
  const { gameId } = useParams();
  const [game, setGame] = useState(null);

  useEffect(() => {
    api.get(`/games/${gameId}`).then(({ data }) => setGame(data)).catch(() => {});
  }, [gameId]);

  if (!game) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-[#545454]">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="game-detail-page">
      <Link to="/games" className="inline-flex items-center gap-1 text-sm font-semibold text-[#920e12] mb-6">
        <ArrowLeft size={16} /> Back to library
      </Link>
      <div className="text-xs uppercase tracking-widest font-extrabold text-[#bf6163]">{levelLabel(game.level)}</div>
      <h1 className="mt-2 font-display text-4xl text-[#550001]">{game.name}</h1>

      <div className="mt-4 flex flex-wrap gap-2">
        {game.skill_sets?.map((s) => (
          <span key={s} className="text-xs px-3 py-1 rounded-full bg-[#ffdedf] text-[#920e12] font-semibold">{s}</span>
        ))}
      </div>

      {ytEmbed(game.video_url) && (
        <div className="mt-6 rounded-2xl overflow-hidden border border-[#dfd8da] bg-black aspect-video" data-testid="game-video">
          <iframe
            className="w-full h-full"
            src={ytEmbed(game.video_url)}
            title={`${game.name} — how to play`}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      )}

      {game.teaser ? (
        <div className="mt-8 bg-white rounded-2xl border border-[#dfd8da] p-8">
          <p className="text-lg text-[#1a1617]">{game.blurb}</p>
          <div className="mt-6 bg-[#221517] text-[#dfd8da] rounded-xl p-6 flex items-start gap-4">
            <Lock className="text-[#bf6163] shrink-0 mt-1" aria-hidden="true" />
            <div>
              <h2 className="text-white font-bold text-lg">Full details are gated</h2>
              <p className="mt-1 text-sm">Description, example, and ways to play without speaking unlock for enrolled participants and directors.</p>
              <div className="mt-4 flex gap-2">
                <Button asChild className="bg-[#920e12] text-white"><Link to="/shop">Enroll</Link></Button>
                <Button asChild variant="outline" className="border-[#bf6163] text-[#ffdedf] bg-transparent"><Link to="/login">Log in</Link></Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-8 space-y-6">
          <section className="bg-white rounded-2xl border border-[#dfd8da] p-7">
            <h2 className="text-sm uppercase tracking-widest font-extrabold text-[#920e12]">Description</h2>
            <p className="mt-2 text-base sm:text-lg text-[#1a1617] leading-relaxed">{game.description}</p>
          </section>
          <section className="bg-white rounded-2xl border border-[#dfd8da] p-7">
            <h2 className="text-sm uppercase tracking-widest font-extrabold text-[#920e12]">Example</h2>
            <p className="mt-2 text-base text-[#1a1617] leading-relaxed italic">{game.example}</p>
          </section>
          <NonSpeakingPanel text={game.non_speaking_modification} />
        </div>
      )}
    </div>
  );
}
